import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Footer from '../Apm/Other/Footer';
import { PublicContainer, publicPalette } from './PublicTheme';

export { PublicContainer, publicPalette };

export const PublicBackdrop = ({ children, sx, ...props }) => (
  <Box
    {...props}
    sx={{
      position: 'relative',
      isolation: 'isolate',
      overflow: 'hidden',
      color: publicPalette.text,
      backgroundColor: publicPalette.background,
      backgroundImage: `
        radial-gradient(circle at 15% 10%, ${alpha(publicPalette.primary, 0.18)} 0, transparent 32%),
        radial-gradient(circle at 85% 18%, ${alpha(publicPalette.secondary, 0.18)} 0, transparent 30%),
        linear-gradient(${alpha('#94A3B8', 0.045)} 1px, transparent 1px),
        linear-gradient(90deg, ${alpha('#94A3B8', 0.045)} 1px, transparent 1px)
      `,
      backgroundSize: 'auto, auto, 52px 52px, 52px 52px',
      '&::before': {
        content: '""',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        background: 'linear-gradient(180deg, rgba(5,9,20,0.05) 0%, rgba(5,9,20,0.9) 100%)',
        pointerEvents: 'none',
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const TechBadge = ({ children, sx }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      width: 'fit-content',
      px: 1.5,
      py: 0.75,
      border: `1px solid ${alpha(publicPalette.primary, 0.28)}`,
      borderRadius: 999,
      color: '#C9F1FF',
      backgroundColor: alpha(publicPalette.primary, 0.08),
      backdropFilter: 'blur(12px)',
      ...sx,
    }}
  >
    <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: publicPalette.primary }} />
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em' }}>
      {children}
    </Typography>
  </Stack>
);

export const SectionHeading = ({ eyebrow, title, description, align = 'left', sx }) => (
  <Stack
    spacing={1.5}
    sx={{
      maxWidth: 760,
      textAlign: align,
      alignItems: align === 'center' ? 'center' : 'flex-start',
      ...sx,
    }}
  >
    {eyebrow && (
      <Typography
        sx={{
          color: publicPalette.primary,
          fontSize: 13,
          fontWeight: 800,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography
      component="h2"
      sx={{
        color: publicPalette.text,
        fontSize: { xs: 32, sm: 40, md: 50 },
        lineHeight: 1.08,
        fontWeight: 800,
        letterSpacing: '-0.035em',
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        sx={{
          color: publicPalette.muted,
          fontSize: { xs: 16, md: 18 },
          lineHeight: 1.75,
        }}
      >
        {description}
      </Typography>
    )}
  </Stack>
);

export const GlassCard = ({ children, sx, ...props }) => (
  <Box
    {...props}
    sx={{
      border: `1px solid ${publicPalette.border}`,
      borderRadius: 4,
      background: `linear-gradient(145deg, ${alpha('#FFFFFF', 0.075)}, ${alpha('#FFFFFF', 0.025)})`,
      boxShadow: '0 24px 70px rgba(0, 0, 0, 0.28)',
      backdropFilter: 'blur(18px)',
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const PublicPageHero = ({ eyebrow, title, description, actions, compact = false }) => (
  <PublicBackdrop>
    <PublicContainer
      sx={{
        pt: { xs: compact ? 7 : 10, md: compact ? 10 : 14 },
        pb: { xs: compact ? 6 : 9, md: compact ? 8 : 12 },
      }}
    >
      <Stack spacing={3} alignItems="center" textAlign="center" sx={{ maxWidth: 900, mx: 'auto' }}>
        {eyebrow && <TechBadge>{eyebrow}</TechBadge>}
        <Typography
          component="h1"
          sx={{
            maxWidth: 900,
            fontSize: { xs: 40, sm: 54, md: 68 },
            lineHeight: 1.02,
            fontWeight: 850,
            letterSpacing: '-0.05em',
            background: 'linear-gradient(110deg, #FFFFFF 12%, #BDEBFF 48%, #CDBDFF 86%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography sx={{ maxWidth: 760, color: publicPalette.muted, fontSize: { xs: 17, md: 20 }, lineHeight: 1.7 }}>
            {description}
          </Typography>
        )}
        {actions}
      </Stack>
    </PublicContainer>
  </PublicBackdrop>
);

export const BackToHome = () => {
  const { t } = useTranslation();

  return (
    <Button
      component={NavLink}
      to="/"
      startIcon={<ArrowBackRoundedIcon />}
      sx={{
        color: publicPalette.muted,
        textTransform: 'none',
        fontWeight: 700,
        '&:hover': { color: publicPalette.text, backgroundColor: alpha('#FFFFFF', 0.05) },
      }}
    >
      {t('button.back_to_main_page')}
    </Button>
  );
};

export const PublicPage = ({ children, showFooter = true, sx }) => (
  <Box
    component="main"
    sx={{
      width: '100%',
      minHeight: '100vh',
      color: publicPalette.text,
      backgroundColor: publicPalette.background,
      ...sx,
    }}
  >
    {children}
    {showFooter && <Footer />}
  </Box>
);
